import React from 'react';
import { Dimensions, ListRenderItem, StyleSheet, View } from 'react-native';

import { UserAvatar } from '@components/Avatar';
import Checkbox from '@components/Checkbox';
import Divider from '@components/Divider';
import FlatList, { FlatListProps } from '@components/FlatList';
import IconButton from '@components/IconButton';
import Modal from '@components/Modal';
import Placeholder, { EmptySearchPlaceholder } from '@components/Placeholders';
import { PressableOpacity } from '@components/Pressable';
import SearchBar from '@components/SearchBar';
import Surface from '@components/Surface';
import { Caption, Subheading, Title } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { sortBy, sortByDisplayName } from '@helpers/Formatters';
import { useLocalSearch } from '@helpers/LocalSearch';
import { Person, User } from '@types';
import { Group } from '@types';
import { Country, Place } from '@types';

const AVATAR_SIZE = 30;

type KeysMatching<T, V> = {
  [K in keyof T]: T[K] extends V ? K : never;
}[keyof T];

type PickerStringKey<T> = Extract<KeysMatching<T, string>, string>;

type BasePickerModalProps<T extends object> = Omit<
  React.ComponentProps<typeof Modal>,
  'children'
> & {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  itemKey: PickerStringKey<T>;
  itemText: PickerStringKey<T>;
  searchKeys: PickerStringKey<T>[];
  searchPlaceholder: string;
  onPress: (item: T) => void;
  isSelected: (item: T) => boolean;
  title: string;
  caption: string;
  emptyComponent?: FlatListProps<T>['ListEmptyComponent'];
};

export function BasePickerModal<T extends object>({
  items,
  renderItem,
  itemKey,
  itemText,
  searchKeys,
  searchPlaceholder,
  onPress,
  isSelected,
  title,
  caption,
  onDismiss,
  emptyComponent,
  style,
  ...props
}: BasePickerModalProps<T>) {
  const [searchText, setSearchText] = React.useState<string>('');
  const { loading: searchLoading, results: filteredItems } = useLocalSearch<T>({
    items,
    keys: searchKeys,
    query: searchText,
  });
  const appTheme = useAppTheme();

  const renderListItem: ListRenderItem<T> = ({ item }) => {
    return (
      <View style={[styles.itemContainer, styles.row, styles.flex]}>
        <Checkbox
          status={isSelected(item) ? 'checked' : 'unchecked'}
          onPress={() => onPress(item)}
        />
        <PressableOpacity style={[styles.row, styles.flex]} onPress={() => onPress(item)}>
          {renderItem(item)}
        </PressableOpacity>
      </View>
    );
  };

  const keyExtractor = (item: T, index: number) => getPickerStringValue(item, itemKey);

  return (
    <Modal onDismiss={onDismiss} contentContainerStyle={styles.modalContent} {...props}>
      <Surface
        style={[
          styles.surface,
          { width: 0.9 * Math.min(Dimensions.get('window').width, appTheme.maxWidth) },
        ]}>
        <IconButton
          icon="close"
          style={styles.closeButton}
          onPress={onDismiss}
          testID="components.Picker.IconButton.Close"
        />
        <Title style={styles.title}>{title}</Title>
        <SearchBar
          value={searchText}
          placeholder={searchPlaceholder}
          onChangeText={(text) => setSearchText(text)}
          loading={searchLoading}
          style={[styles.searchBar, { backgroundColor: appTheme.colors.background }]}
        />
        <FlatList
          data={filteredItems}
          renderItem={renderListItem}
          keyExtractor={keyExtractor}
          style={styles.list}
          ItemSeparatorComponent={() => <Divider />}
          ListEmptyComponent={
            searchText.length === 0 && emptyComponent ? emptyComponent : <EmptySearchPlaceholder />
          }
          ListHeaderComponent={<Caption style={styles.caption}>{caption}</Caption>}
        />
      </Surface>
    </Modal>
  );
}

type SinglePickerModalProps<T extends object> = Omit<
  BasePickerModalProps<T>,
  'onPress' | 'isSelected'
> & {
  selectedItem: T;
  setSelectedItem: (item: T) => void;
};

export function SinglePickerModal<T extends object>({
  selectedItem,
  setSelectedItem,
  itemKey,
  onDismiss,
  ...props
}: SinglePickerModalProps<T>) {
  const onPress = (item: T) => {
    setSelectedItem(item);
    onDismiss?.();
  };
  const selectedKey = getPickerStringValue(selectedItem, itemKey);
  const isSelected = (item: T) => getPickerStringValue(item, itemKey) === selectedKey;
  return (
    <BasePickerModal
      onPress={onPress}
      isSelected={isSelected}
      itemKey={itemKey}
      onDismiss={onDismiss}
      {...props}
    />
  );
}

type MultiPickerModalProps<T extends object> = Omit<
  BasePickerModalProps<T>,
  'onPress' | 'isSelected'
> & {
  selectedItems: Set<string>;
  setSelectedItems: (selectedItems: Set<string>) => void;
};

export function MultiPickerModal<T extends object>({
  selectedItems,
  setSelectedItems,
  itemKey,
  ...props
}: MultiPickerModalProps<T>) {
  const onPress = (item: T) => {
    const itemIds = new Set(selectedItems);
    const selectedKey = getPickerStringValue(item, itemKey);
    if (selectedItems.has(selectedKey)) {
      itemIds.delete(selectedKey);
    } else {
      itemIds.add(selectedKey);
    }
    setSelectedItems(itemIds);
  };
  const isSelected = (item: T) => selectedItems.has(getPickerStringValue(item, itemKey));
  return <BasePickerModal onPress={onPress} isSelected={isSelected} itemKey={itemKey} {...props} />;
}

function getPickerStringValue<T extends object>(item: T, key: PickerStringKey<T>) {
  const value = item[key];
  return typeof value === 'string' ? value : String(value);
}

type LocationPickerModalProps = {
  locations: Place[];
  selectedLocations: Set<string>;
  setSelectedLocations: (selectedLocations: Set<string>) => void;
  visible: boolean;
  onDismiss: () => void;
};

export function LocationPickerModal({
  locations,
  selectedLocations,
  setSelectedLocations,
  visible,
  onDismiss,
}: LocationPickerModalProps) {
  return (
    <MultiPickerModal
      items={sortBy(locations, 'displayName', 'asc')}
      selectedItems={selectedLocations}
      setSelectedItems={setSelectedLocations}
      title="Locations"
      caption="Tap on a location to select"
      itemKey="placeId"
      itemText="displayName"
      searchKeys={['displayName']}
      searchPlaceholder="Search by location"
      renderItem={(item) => (
        <Subheading numberOfLines={1} style={[styles.subheading, styles.flex]}>
          {item.displayName}
        </Subheading>
      )}
      visible={visible}
      onDismiss={onDismiss}
    />
  );
}

type UserPickerModalProps = {
  users: User[];
  selectedUsers: Set<string>;
  setSelectedUsers: (selectedUsers: Set<string>) => void;
  visible: boolean;
  onDismiss: () => void;
};

export function UserPickerModal({
  users,
  selectedUsers,
  setSelectedUsers,
  visible,
  onDismiss,
}: UserPickerModalProps) {
  return (
    <MultiPickerModal
      items={sortByDisplayName(users)}
      selectedItems={selectedUsers}
      setSelectedItems={setSelectedUsers}
      title="Friends"
      caption="Tap on a friend to select"
      itemKey="userId"
      itemText="displayName"
      searchKeys={['displayName']}
      searchPlaceholder="Search by name"
      renderItem={(item) => (
        <>
          <UserAvatar
            displayName={item.displayName}
            photoUrl={item.photoUrl}
            size={AVATAR_SIZE}
            style={styles.avatar}
          />
          <Subheading numberOfLines={1} style={[styles.flex, styles.subheading]}>
            {item.displayName}
          </Subheading>
        </>
      )}
      visible={visible}
      onDismiss={onDismiss}
    />
  );
}

type PersonPickerModalProps = {
  people: Person[];
  selectedPeople: Set<string>;
  setSelectedPeople: (selectedPeople: Set<string>) => void;
  visible: boolean;
  onDismiss: () => void;
  title?: string;
  caption?: string;
};

export function PersonPickerModal({
  people,
  selectedPeople,
  setSelectedPeople,
  visible,
  onDismiss,
}: PersonPickerModalProps) {
  return (
    <MultiPickerModal
      items={sortByDisplayName(people)}
      selectedItems={selectedPeople}
      setSelectedItems={setSelectedPeople}
      title="People"
      caption="Tap on a person to select"
      itemKey="personId"
      itemText="displayName"
      searchKeys={['displayName']}
      searchPlaceholder="Search by name"
      renderItem={(item) => (
        <>
          <UserAvatar
            displayName={item.displayName}
            photoUrl={item.photoUrl}
            size={AVATAR_SIZE}
            style={styles.avatar}
          />
          <View>
            <Subheading numberOfLines={1} style={[styles.flex, styles.subheading]}>
              {item.displayName}
            </Subheading>
            {item.phoneNumber || item.username ? (
              <Caption style={peoplePickerStyles.caption}>
                {item.phoneNumber || item.username}
              </Caption>
            ) : null}
          </View>
        </>
      )}
      visible={visible}
      onDismiss={onDismiss}
    />
  );
}

type GroupPickerModalProps = {
  groups: Group[];
  selectedGroupIds: Set<string>;
  setSelectedGroupIds: (selectedGroupIds: Set<string>) => void;
  visible: boolean;
  onDismiss: () => void;
};

export function GroupPickerModal({
  groups,
  selectedGroupIds,
  setSelectedGroupIds,
  visible,
  onDismiss,
}: GroupPickerModalProps) {
  return (
    <MultiPickerModal
      items={sortByDisplayName(groups)}
      selectedItems={selectedGroupIds}
      setSelectedItems={setSelectedGroupIds}
      title="Groups"
      caption="Tap on a group to select"
      itemKey="groupId"
      itemText="displayName"
      searchKeys={['displayName', 'description']}
      searchPlaceholder="Search groups"
      renderItem={(item) => (
        <>
          <UserAvatar
            displayName={item.displayName}
            photoUrl={item.photoUrl}
            size={AVATAR_SIZE}
            style={styles.avatar}
          />
          <Subheading numberOfLines={1} style={[styles.flex, styles.subheading]}>
            {item.displayName}
          </Subheading>
        </>
      )}
      visible={visible}
      onDismiss={onDismiss}
      emptyComponent={
        <Placeholder
          title="No groups"
          message="Join a group to share your plans"
          icon="account-multiple"
        />
      }
    />
  );
}

type CountryPickerModalProps = {
  selectedCountry: Country;
  setSelectedCountry: (country: Country) => void;
  visible: boolean;
  onDismiss: () => void;
};

export function CountryPickerModal({
  selectedCountry,
  setSelectedCountry,
  visible,
  onDismiss,
}: CountryPickerModalProps) {
  const countries = require('../assets/countries.json');

  return (
    <SinglePickerModal
      items={countries}
      selectedItem={selectedCountry}
      setSelectedItem={setSelectedCountry}
      title="Countries"
      caption="Tap on a country to select"
      itemKey="code"
      itemText="name"
      searchKeys={['name', 'dial_code']}
      searchPlaceholder="Search countries"
      renderItem={(item) => (
        <Subheading numberOfLines={1} style={[styles.flex, styles.subheading]}>
          {item.flag + ' ' + item.name + ` (${item.dial_code})`}
        </Subheading>
      )}
      visible={visible}
      onDismiss={onDismiss}
    />
  );
}

const styles = StyleSheet.create({
  avatar: {
    marginHorizontal: 5,
  },
  caption: {
    textAlign: 'center',
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 1,
  },
  flex: {
    flex: 1,
  },
  itemContainer: {
    paddingVertical: 2,
  },
  list: {
    marginTop: 5,
  },
  modalContent: {
    alignSelf: 'center',
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  searchBar: {
    marginVertical: 5,
  },
  subheading: {
    textAlignVertical: 'center',
  },
  surface: {
    height: 0.5 * Dimensions.get('window').height,
    padding: 10,
  },
  title: {
    textAlign: 'center',
  },
});

const peoplePickerStyles = StyleSheet.create({
  caption: {
    marginVertical: 0,
  },
});
